import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import CandleFlame from './CandleFlame';
import { lightCandle, getCandleCount } from '../../services/memorialApi';
import './CandleButton.css';

// Storage key for personal lit candles (to track which ones YOU lit)
const STORAGE_KEY = 'memorialLitCandles';

/**
 * Get personal lit candles from localStorage
 * @returns {Object} Map of memorial IDs to candle data
 */
const getLitCandles = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading lit candles from localStorage:', e);
  }
  return {};
};

/**
 * Save personal lit candle to localStorage
 * @param {number} memorialId - The memorial ID
 */
const savePersonalCandle = (memorialId) => {
  try {
    const candles = getLitCandles();
    candles[memorialId] = {
      lit: true,
      litAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(candles));
    return candles;
  } catch (e) {
    console.error('Error saving lit candle to localStorage:', e);
    return null;
  }
};

/**
 * Check if the current user has lit a candle for a memorial
 * @param {number} memorialId - The memorial ID
 * @returns {boolean} True if the user has lit this candle
 */
export const isCandelLit = (memorialId) => {
  const candles = getLitCandles();
  return !!candles[memorialId];
};

/**
 * CandleButton component for lighting a Yahrzeit candle in memory
 * @param {Object} props - Component props
 * @param {number} props.memorialId - The memorial ID
 * @param {string} props.memorialName - The name of the person (for accessibility)
 * @param {Function} props.onLit - Callback when candle is lit
 */
const CandleButton = ({ memorialId, memorialName, onLit }) => {
  const [isLit, setIsLit] = useState(false); // Personal: did YOU light it
  const [count, setCount] = useState(0); // Shared: total candles lit by everyone
  const [showTooltip, setShowTooltip] = useState(false);
  const [justLit, setJustLit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check personal state and fetch shared count on mount
  useEffect(() => {
    const personallyLit = isCandelLit(memorialId);
    setIsLit(personallyLit);

    // Fetch shared count from server
    getCandleCount(memorialId)
      .then((serverCount) => {
        setCount(serverCount);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [memorialId]);

  // Handle lighting the candle
  const handleLight = useCallback(async () => {
    if (!isLit) {
      // Save personal record
      savePersonalCandle(memorialId);
      setIsLit(true);
      setJustLit(true);
      setShowTooltip(true);

      // Update shared count on server
      try {
        const result = await lightCandle(memorialId);
        setCount(result.count);
      } catch (error) {
        // Still show as lit locally even if server fails
        setCount((prev) => prev + 1);
      }

      // Hide tooltip after 3 seconds
      setTimeout(() => {
        setShowTooltip(false);
      }, 3000);

      // Reset "just lit" animation after it completes
      setTimeout(() => {
        setJustLit(false);
      }, 1500);

      // Call the onLit callback if provided
      if (onLit) {
        onLit(memorialId);
      }
    }
  }, [isLit, memorialId, onLit]);

  const hasAnyCandles = count > 0 || isLit;

  return (
    <div className="candle-button-container">
      <button
        className={`candle-button ${hasAnyCandles ? 'lit' : ''} ${justLit ? 'just-lit' : ''}`}
        onClick={handleLight}
        disabled={isLit}
        aria-label={
          isLit
            ? `You lit a candle in memory of ${memorialName}. ${count} total candles lit.`
            : `Light a candle in memory of ${memorialName}. ${count} candles already lit.`
        }
        aria-pressed={isLit}
        title={isLit ? `Your candle is burning (${count} total)` : `Light a candle (${count} lit)`}
      >
        {hasAnyCandles ? (
          <CandleFlame size={22} className={justLit ? 'lighting' : ''} />
        ) : (
          <span className="candle-icon-unlit">
            {/* Unlit candle icon */}
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="10"
                y="10"
                width="4"
                height="12"
                rx="0.5"
                fill="#e8dcc8"
                stroke="#c4b8a8"
                strokeWidth="0.5"
              />
              <rect x="11.5" y="8" width="1" height="2" fill="#333" />
              {/* Subtle smoke wisps */}
              <path
                d="M12 6C12 6 12.5 4 12 3"
                stroke="#999"
                strokeWidth="0.5"
                strokeLinecap="round"
                opacity="0.5"
              />
            </svg>
          </span>
        )}
      </button>

      {/* Count badge */}
      {!isLoading && count > 0 && (
        <span className="candle-count" aria-hidden="true">
          {count}
        </span>
      )}

      {/* Tooltip */}
      {showTooltip && (
        <div className="candle-tooltip" role="status" aria-live="polite">
          {isLit ? 'Your candle is burning' : `${count} candles lit`}
        </div>
      )}
    </div>
  );
};

CandleButton.propTypes = {
  memorialId: PropTypes.number.isRequired,
  memorialName: PropTypes.string.isRequired,
  onLit: PropTypes.func,
};

export default CandleButton;
export { getLitCandles };
